// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — Yjs binding (lazy per-doc CRDT).
//
// Design rule from the gift summary: "Yjs Docs are LAZY — only build a Y.Doc
// when the user actually edits a doc. Store snapshots on idle." Implementation:
//
//   • A live `Y.Doc` is created on first call to `bindDocToYjs(docId)` and
//     cached in a module-level `Map`. Same id → same Doc forever (or until
//     explicit `unbindDoc(docId)`).
//   • The Y.Doc rehydrates from `yjs_snapshot` in Dexie if a row exists,
//     otherwise it seeds itself from the doc's current `content` field.
//   • An `update` listener schedules an idle-time snapshot write back to
//     Dexie. We use `requestIdleCallback` when available, falling back to a
//     `setTimeout(…, 1500)` so the snapshot path still works in tests and in
//     Safari (which does not implement rIC).
//
// Snapshots are stored as full-state buffers (`Y.encodeStateAsUpdate(doc)`),
// not deltas — for single-user-with-occasional-WebRTC-collab the storage cost
// (~2-4× the markdown size) is dwarfed by the simplicity of "load one buffer,
// `Y.applyUpdate`, done."
//
// ─── snapshot HISTORY (shipped at Dexie v2, 2026-04-27) ─────────────────────
//
// Each `persistSnapshot` call does TWO writes:
//
//   1. Overwrite `yjs_snapshot` (pk=docId) — the "latest" pointer used by
//      `bindDocToYjs` for fast doc-open hydration. Single row per doc.
//   2. Append a row to `yjs_snapshot_log` (pk=[docId+ts]) — append-only,
//      per-doc capped at LOG_LIMIT entries via a tail-trim after each write.
//
// `listSnapshots(docId)` queries the log table directly — the log IS the
// source of truth for history (no in-memory cache layered on top).
// `restoreSnapshot(docId, ts)` fetches the [docId+ts] row and rehydrates the
// historical content. CRDT semantics: see the `restoreSnapshot` doc comment —
// this is a content-overwrite under transact(), not a CRDT rewind.

import * as Y from 'yjs';
import { getDB } from './dexie-schema';
import type { VaultDoc, YjsSnapshot } from './schema';
import type { YjsSnapshotLogRow } from './dexie-schema';

// ─── module state ───────────────────────────────────────────────────────────

interface BoundDoc {
  yDoc: Y.Doc;
  /** Unsubscribe the update listener on `unbindDoc`. */
  unsub: () => void;
  /** Pending idle handle (number for setTimeout, opaque for rIC). */
  pending: number | null;
}

const bound = new Map<string, BoundDoc>();

/**
 * One persisted snapshot, surfaced to the EditHistoryViewer. Mirrors a row in
 * the Dexie `yjs_snapshot_log` table (the v2 source of truth). The
 * `persisted` flag is always `true` now — every entry IS persisted to disk —
 * but the field is retained so the existing UI's `disk` badge logic doesn't
 * have to change shape.
 */
export interface SnapshotRecord {
  docId: string;
  /** ms epoch when the snapshot was captured. */
  ts: number;
  /** `Y.encodeStateAsUpdate(doc)` at capture time. */
  state: Uint8Array;
  /** Always `true` since the v2 log table replaced the in-memory ring. */
  persisted: boolean;
  /** Optional human-readable label (e.g. "before merge"). */
  label?: string;
}

/** Per-docId cap on rows in `yjs_snapshot_log`. Trim runs after every append
 *  (cheaper than a periodic sweep — see retention notes in
 *  `appendSnapshotLog`). */
export const LOG_LIMIT = 64;

// ─── idle scheduling ────────────────────────────────────────────────────────

interface IdleCapableWindow {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
}

function scheduleIdle(fn: () => void): number {
  const w = globalThis as unknown as IdleCapableWindow;
  if (typeof w.requestIdleCallback === 'function') {
    return w.requestIdleCallback(fn, { timeout: 2000 });
  }
  return setTimeout(fn, 1500) as unknown as number;
}

function cancelIdle(handle: number): void {
  const w = globalThis as unknown as IdleCapableWindow;
  if (typeof w.cancelIdleCallback === 'function') {
    try {
      w.cancelIdleCallback(handle);
      return;
    } catch {
      /* fall through */
    }
  }
  clearTimeout(handle);
}

// ─── public API ─────────────────────────────────────────────────────────────

/**
 * Lazily bind a Y.Doc to a vault doc id. Returns the same Y.Doc on every
 * subsequent call, so multiple Solid components can share editor state without
 * collaboration glue.
 *
 * The returned Y.Doc has a single shared text under key `'content'` —
 * use `yDoc.getText('content')` to bind it to a Y-aware editor (CodeMirror,
 * ProseMirror via y-prosemirror, etc.).
 *
 * Behaviour:
 *   1. If a snapshot exists in Dexie's `yjs_snapshot` table, applies it.
 *   2. Otherwise, if `seedFromDoc` is provided, seeds the shared text from
 *      `seedFromDoc.content`. Pass the `VaultDoc` you have on hand — saves a
 *      Dexie roundtrip.
 *   3. Otherwise, returns an empty Y.Doc — the caller should populate it.
 */
export async function bindDocToYjs(
  docId: string,
  seedFromDoc?: Pick<VaultDoc, 'content'>,
): Promise<Y.Doc> {
  const existing = bound.get(docId);
  if (existing) return existing.yDoc;

  const yDoc = new Y.Doc();
  const db = getDB();
  const snap = await db.yjs_snapshot.get(docId);

  if (snap) {
    Y.applyUpdate(yDoc, snap.state);
  } else if (seedFromDoc) {
    yDoc.getText('content').insert(0, seedFromDoc.content);
  }

  // Schedule a snapshot whenever the doc mutates. We coalesce: cancel any
  // pending idle task and reschedule, so a flurry of keystrokes produces one
  // write per idle window, not one per keystroke.
  const onUpdate = (): void => {
    const slot = bound.get(docId);
    if (!slot) return;
    if (slot.pending != null) cancelIdle(slot.pending);
    slot.pending = scheduleIdle(() => {
      slot.pending = null;
      void persistSnapshot(docId, yDoc);
    });
  };
  yDoc.on('update', onUpdate);
  const unsub = (): void => yDoc.off('update', onUpdate);

  bound.set(docId, { yDoc, unsub, pending: null });
  return yDoc;
}

/** Force an immediate snapshot write — call before navigation away. */
export async function flushDocYjs(docId: string): Promise<void> {
  const slot = bound.get(docId);
  if (!slot) return;
  if (slot.pending != null) {
    cancelIdle(slot.pending);
    slot.pending = null;
  }
  await persistSnapshot(docId, slot.yDoc);
}

/** Encoded full state of the bound Y.Doc — cheap to send over WS. */
export async function getDocYjsState(docId: string): Promise<Uint8Array> {
  const slot = bound.get(docId);
  if (slot) return Y.encodeStateAsUpdate(slot.yDoc);
  // Not bound — read the latest snapshot directly.
  const snap = await getDB().yjs_snapshot.get(docId);
  return snap ? snap.state : new Uint8Array();
}

/** Dispose the live Y.Doc. Pending writes flush first. */
export async function unbindDoc(docId: string): Promise<void> {
  const slot = bound.get(docId);
  if (!slot) return;
  if (slot.pending != null) {
    cancelIdle(slot.pending);
    slot.pending = null;
    await persistSnapshot(docId, slot.yDoc);
  }
  slot.unsub();
  slot.yDoc.destroy();
  bound.delete(docId);
}

/** Test helper — drop all bindings without touching Dexie. */
export function _resetBindingsForTests(): void {
  for (const slot of bound.values()) {
    if (slot.pending != null) cancelIdle(slot.pending);
    slot.unsub();
    slot.yDoc.destroy();
  }
  bound.clear();
}

// ─── internal ───────────────────────────────────────────────────────────────

async function persistSnapshot(docId: string, yDoc: Y.Doc): Promise<void> {
  const state = Y.encodeStateAsUpdate(yDoc);
  const ts = Date.now();
  const row: YjsSnapshot = {
    docId,
    state,
    updatedAt: ts,
  };
  // 1. Overwrite the "latest" pointer (single row per doc, used by
  //    `bindDocToYjs` for fast hydration on doc-open).
  await getDB().yjs_snapshot.put(row);
  // 2. Append a row to the persistent log (per-docId capped at LOG_LIMIT).
  await appendSnapshotLog(docId, ts, state);
}

/**
 * Append one row to `yjs_snapshot_log`, then trim the tail beyond LOG_LIMIT.
 *
 * Why trim after every write rather than periodically? Two reasons:
 *   • The trim is a cheap indexed range delete (`where('docId').equals(...)`
 *     is O(log N) seek + linear walk); a periodic sweep would have to scan
 *     ALL docs, not just the one we just wrote.
 *   • It keeps the steady-state row count exactly bounded — a periodic
 *     sweep can let a runaway docId grow unboundedly between sweeps.
 *
 * Exported for the schema test that exercises the retention path directly.
 */
export async function appendSnapshotLog(
  docId: string,
  ts: number,
  update: Uint8Array,
  label?: string,
): Promise<void> {
  const db = getDB();
  const row: YjsSnapshotLogRow = {
    docId,
    ts,
    update,
    byteLength: update.byteLength,
    ...(label !== undefined ? { label } : {}),
  };
  await db.yjs_snapshot_log.put(row);

  // Retention: keep only the most recent LOG_LIMIT entries for this docId.
  // The `docId` index gives us "all rows of this doc" cheaply; we sort by
  // `ts` in JS (the docId index doesn't carry secondary order on its own)
  // and delete the tail past the cap by composite-pk anyOf.
  const tsList = await db.yjs_snapshot_log
    .where('docId')
    .equals(docId)
    .toArray()
    .then((rows) => rows.map((r) => r.ts).sort((a, b) => b - a));
  if (tsList.length > LOG_LIMIT) {
    const tailTs = tsList.slice(LOG_LIMIT); // oldest entries past the cap
    await db.yjs_snapshot_log
      .where('[docId+ts]')
      .anyOf(tailTs.map((t) => [docId, t]))
      .delete();
  }
}

// ─── history surface ────────────────────────────────────────────────────────

/**
 * Return the persisted snapshot history for a doc, newest-first. Reads
 * directly from Dexie's `yjs_snapshot_log` — the log table IS the source of
 * truth (no in-memory cache layered on top).
 *
 * Backward-compat: signature unchanged from v1. The `persisted` flag on each
 * record is always `true` now (every row IS persisted to disk).
 */
export async function listSnapshots(docId: string): Promise<SnapshotRecord[]> {
  const rows = await getDB()
    .yjs_snapshot_log.where('docId')
    .equals(docId)
    .toArray();
  rows.sort((a, b) => b.ts - a.ts); // newest first

  return rows.map((r) => ({
    docId: r.docId,
    ts: r.ts,
    state: r.update,
    persisted: true,
    ...(r.label !== undefined ? { label: r.label } : {}),
  }));
}

/**
 * Restore a doc to a historical snapshot.
 *
 * CRDT semantics: this is NOT a "rewind". Yjs is conflict-free — applying an
 * old update onto the current Y.Doc would MERGE the two states (the CRDT
 * keeps causal information so concurrent edits both survive). To present
 * "rewind"-like UX, we extract the historical plain text from a temporary
 * Y.Doc and overwrite the live Y.Doc's shared text in a single transact()
 * block. A future contributor designing a "true rewind" UI should NOT skip
 * this step — applying the snapshot directly would silently merge, not roll
 * back, and the user-visible state would be the union of new + old edits.
 *
 * Step 1 captures an undo-point snapshot of the CURRENT state first, so the
 * user can pick that fresh row from the history list to reverse the restore.
 */
export async function restoreSnapshot(
  docId: string,
  snapshotTs: number,
): Promise<void> {
  const db = getDB();
  const slot = bound.get(docId);

  // Fetch the target log row directly by composite pk.
  const target = await db.yjs_snapshot_log.get([docId, snapshotTs]);
  if (!target) {
    throw new Error(
      `restoreSnapshot: no snapshot at ts=${snapshotTs} for ${docId}`,
    );
  }

  // 1. Capture an undo-point snapshot of the current state (if bound).
  if (slot) {
    if (slot.pending != null) {
      cancelIdle(slot.pending);
      slot.pending = null;
    }
    await persistSnapshot(docId, slot.yDoc);
  }

  // 2. Build a temporary Y.Doc from `target.update` to extract the historical
  //    plain-text content. This is the CRDT-safe way to read "what the doc
  //    looked like at that snapshot" without merging it into the live Doc.
  const restored = new Y.Doc();
  Y.applyUpdate(restored, target.update);
  const restoredText = restored.getText('content').toString();

  if (slot) {
    // 3a. Mutate the LIVE Y.Doc in place: clear the shared text and re-insert
    //     the historical content. This preserves identity for any downstream
    //     observers (e.g. y-prosemirror) that hold a reference to the Y.Text.
    const yText = slot.yDoc.getText('content');
    slot.yDoc.transact(() => {
      yText.delete(0, yText.length);
      yText.insert(0, restoredText);
    });
    // The transact() above triggers the existing `update` listener, which
    // schedules a new idle snapshot of the post-restore state.
  } else {
    // 3b. Not bound — persist the historical state as the new "latest"
    //     pointer + log entry so a future bind picks it up.
    const ts = Date.now();
    const row: YjsSnapshot = {
      docId,
      state: target.update,
      updatedAt: ts,
    };
    await db.yjs_snapshot.put(row);
    await appendSnapshotLog(docId, ts, target.update);
  }

  restored.destroy();
}

/** Test helper — drop all log rows. v2: now a Dexie clear, not a Map clear. */
export async function _resetSnapshotHistoryForTests(): Promise<void> {
  await getDB().yjs_snapshot_log.clear();
}

/** Pull the current shared-text content out of a Y.Doc. */
export function readYDocContent(yDoc: Y.Doc): string {
  return yDoc.getText('content').toString();
}
